import {Form, FormField, FormSubmitHandler, Separator, showNotification} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';
import {array, boolean, object} from 'yup';

import {UseFormReturn} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetBillingInformationV2Query,
  useGetTenantV2Query,
  usePostBillingInformationMutation,
  usePutBillingInformationMutation,
} from '@dms/api/base';
import {AddrDetailApiResponse} from '@dms/api/core';
import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {AddressSearch, handleApiError, useTenant} from '@dms/shared';

import {useNavigate, yupString} from 'shared/web';

import {BankAccountList} from './components/BankAccountList';
import {DeputyPersonList} from './components/DeputyPersonList';
import type {BillingInformationDetailFormValues} from './types/BillingInformationDetailFormValues';
import {getIsBankAccountError} from './utils/getIsBankAccountError';

const breadcrumbs = [
  {
    label: i18n.t('entity.customer.labels.billingInformation'),
    href: settingsRoutes.billingInformation,
  },
];

export function BillingInformationDetail() {
  const {id} = useParams();
  const navigate = useNavigate();

  const isCreating = isNil(id);

  const [createBillingInfo] = usePostBillingInformationMutation();
  const [updateBillingInfo] = usePutBillingInformationMutation();
  const {data: tenant} = useGetTenantV2Query();
  const {
    data: billingInformation,
    isLoading,
    isError,
  } = useGetBillingInformationV2Query({id: id ?? ''}, {skip: isCreating});

  const {tenantCountry} = useTenant();
  const setAddressFormSearch =
    (formApi: UseFormReturn<BillingInformationDetailFormValues>) =>
    (address: AddrDetailApiResponse) => {
      if (isNil(tenant)) {
        showNotification.error();
        return;
      }

      formApi.setValue(
        'contactInformation.address',
        {
          city: address?.city ?? '',
          street: address?.street ?? '',
          zip: address?.zipCode ?? '',
          state: address?.state ?? '',
          prefix: address?.prefix ?? '',
          descriptiveNumber: address?.descriptiveNumber ?? null,
          orientationNumber: address?.orientationNumber ?? null,
          addressComplement: null,
          district: null,
          country: tenant.contactInformation.address.country,
          coordinates: null,
          postal: null,
        },
        {shouldValidate: true}
      );
    };

  const handleSubmit: FormSubmitHandler<BillingInformationDetailFormValues> = async (
    values,
    setError,
    _reset,
    blockerContext
  ) => {
    await (isCreating ? createBillingInfo(values) : updateBillingInfo({id: id ?? '', ...values}))
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.billingInformation);
      })
      .catch((error: ApiError) => {
        const isBankAccountError = getIsBankAccountError(error, values, setError);
        handleApiError(error, {
          signalApiError: blockerContext?.reportApiFailure,
          silent: isBankAccountError,
        });
      });
  };

  const title = isCreating
    ? i18n.t('entity.customer.actions.newBillingInformation')
    : (billingInformation?.billingName ?? i18n.t('entity.customer.actions.editBillingInformation'));

  const defaultValues = {
    ...billingInformation,
    bankAccounts:
      billingInformation?.bankAccounts.map((acc) => ({...acc, isExpanded: false})) ?? [],
    contactInformation: {
      ...billingInformation?.contactInformation,
      address: {
        street: billingInformation?.contactInformation?.address?.street ?? '',
        city: billingInformation?.contactInformation?.address?.city ?? '',
        zip: billingInformation?.contactInformation?.address?.zip ?? '',
        state: billingInformation?.contactInformation?.address?.state ?? '',
        prefix: billingInformation?.contactInformation?.address?.prefix ?? '',
        descriptiveNumber: billingInformation?.contactInformation?.address?.descriptiveNumber ?? '',
        orientationNumber:
          billingInformation?.contactInformation?.address?.orientationNumber ?? null,
        addressComplement:
          billingInformation?.contactInformation?.address?.addressComplement ?? null,
        district: billingInformation?.contactInformation?.address?.district ?? null,
        country: tenantCountry ?? '',
      },
    },
  };

  const schema = object({
    billingName: yupString.required(),
    contactInformation: object({
      registrationNumber: yupString.required(),
      vatNumber: yupString.required(),
      companyName: yupString.required(),
      fileNumber: yupString.nullable().default(null),
      address: object({
        street: yupString.required(),
        descriptiveNumber: yupString,
        orientationNumber: yupString,
        city: yupString.required(),
        district: yupString,
        zip: yupString.required(),
      }),
    }),
    deputyPersons: array(
      object({
        firstName: yupString.required(),
        lastName: yupString.required(),
      })
    ).default([]),
    bankAccounts: array(
      object({
        accountName: yupString.required(),
        accountNumber: yupString.required(),
        isExpanded: boolean().default(false),
      })
    ).min(1, i18n.t('general.validations.fieldIsRequired')),
  });

  return (
    <SettingsTemplate
      header={{
        title,
        breadcrumbs,
      }}
      data-testid={testIds.settings.billingInformation('detail')}
      isError={isError}
      isCreating={isCreating}
      isLoading={isLoading}
    >
      <Form<BillingInformationDetailFormValues>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        schema={schema}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => {
          const handleClose = () => navigateWithoutBlocker(settingsRoutes.billingInformation);

          return (
            <VStack>
              <SettingsSection>
                <Grid columns={2}>
                  <GridItem span={2}>
                    <FormField
                      type="text"
                      control={control}
                      name="billingName"
                      label={i18n.t('entity.customer.labels.billingName')}
                      isRequired
                    />
                  </GridItem>

                  <FormField
                    type="text"
                    control={control}
                    name="contactInformation.registrationNumber"
                    label={i18n.t('entity.customer.labels.registrationNumber')}
                    isRequired
                  />

                  <FormField
                    type="text"
                    control={control}
                    name="contactInformation.vatNumber"
                    label={i18n.t('entity.customer.labels.vatNumber')}
                    isRequired
                  />

                  <FormField
                    type="text"
                    control={control}
                    name="contactInformation.companyName"
                    label={i18n.t('entity.customer.labels.companyName')}
                    isRequired
                  />

                  <FormField
                    type="text"
                    control={control}
                    name="contactInformation.fileNumber"
                    label={i18n.t('contractInformation.labels.fileNumber')}
                  />
                </Grid>
              </SettingsSection>

              <Separator spacing={5} />

              <SettingsSection>
                <VStack spacing={5}>
                  <Heading size={4}>{i18n.t('entity.address.labels.address')}</Heading>
                  <AddressSearch onSelect={setAddressFormSearch(formApi)} />
                  <Grid columns={4}>
                    <GridItem span={2}>
                      <FormField
                        name="contactInformation.address.street"
                        label={i18n.t('entity.address.labels.street')}
                        control={control}
                        type="text"
                        isRequired
                      />
                    </GridItem>
                    <FormField
                      name="contactInformation.address.descriptiveNumber"
                      label={i18n.t('entity.address.labels.descriptiveNumber')}
                      control={control}
                      type="text"
                    />
                    <FormField
                      name="contactInformation.address.orientationNumber"
                      label={i18n.t('entity.address.labels.orientationNumber')}
                      control={control}
                      type="text"
                    />
                    <GridItem span={2}>
                      <FormField
                        name="contactInformation.address.city"
                        label={i18n.t('entity.address.labels.city')}
                        control={control}
                        type="text"
                        isRequired
                      />
                    </GridItem>

                    <FormField
                      name="contactInformation.address.district"
                      label={i18n.t('entity.address.labels.district')}
                      control={control}
                      type="text"
                    />
                    <FormField
                      name="contactInformation.address.zip"
                      label={i18n.t('entity.address.labels.zipCode')}
                      control={control}
                      type="text"
                      isRequired
                    />
                  </Grid>
                </VStack>
              </SettingsSection>

              <Separator spacing={5} />

              <DeputyPersonList control={control} formApi={formApi} />

              <Separator spacing={5} />

              <BankAccountList control={control} formApi={formApi} />

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.close'),
                    onClick: handleClose,
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.save'),
                  },
                ]}
              />
            </VStack>
          );
        }}
      </Form>
    </SettingsTemplate>
  );
}
